import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react"
import { Trans, useLingui } from "@lingui/react/macro"
import { router } from "@inertiajs/react"
import type { ReloadOptions } from "@inertiajs/core"
import { getCsrfToken } from "@/lib/csrf"
import { camelizeKeys } from "@/lib/utils"
import { toast } from "@/components/ui/sonner"
import { PortalPopover } from "@/components/v2/chrome/portal-popover"
import { SuggestionCard } from "@/components/v2/suggestions/suggestion-card"
import { CALL_DETAIL_RELOAD_PROPS } from "@/lib/voice-call-props"
import { deletePromptCopy } from "@/lib/delete-prompt-copy"
import { methodLabel, COMPOSER_METHODS,
  tomorrowAt9Iso,
  isoToLocalInput,
  localInputToIso,
} from "@/lib/follow-up-method"
import type {
  CallDealsResponse,
  FollowUpSuggestion,
  VoiceCallDeal,
  VoiceCallRecord,
} from "@/types/v2/voice"
import type { AddActivityEmployee } from "./voice-employee"

// ── CallFollowUps (CON-197 §A5) ────────────────────────────
// The shared "AI proposes, human approves" follow-up section.
// Rendered both on the light CallDetailPanel (`theme="light"`)
// and the dark after-call card (`theme="dark"`).
//
// Source of truth is `voiceCall.followUpSuggestions` — an
// ordered, server-derived list (accepted_pending → suggested →
// accepted_completed → dismissed). Every action writes a real
// record the moment it happens (no batch commit): Accept POSTs
// an Activity, Reject POSTs a dismissal, complete/edit/move/
// delete PATCH/DELETE the Activity. Each action patches a local
// optimistic copy FIRST so the surface updates instantly, then
// fires the fetch, then partial-reloads the shared prop
// manifest so every other surface (Today / Focus / deal feed /
// contact feed / NEXT STEPS) reconciles. Accept and Reject also
// raise a Sonner undo toast (~6s) that reverses both.

interface CallFollowUpsProps {
  voiceCall: VoiceCallRecord
  // light → CallDetailPanel (cream/charcoal); dark → the
  // after-call card (the VoiceWidgetShell's dark palette).
  theme: "light" | "dark"
  currentEmployee: AddActivityEmployee
  ownerOptions: AddActivityEmployee[]
}

// ── Theme palettes ─────────────────────────────────────────
// Exact hexes from the Paper artboards (light = Artboard 1's
// "Follow-up · approve / reject / undo / bring-back" section;
// dark = the post-call card's existing palette).
interface FollowUpTheme {
  suggestedBg: string
  suggestedBorder: string
  rowBorder: string
  rowBg: string
  checkboxBorder: string
  completedCheckBg: string
  completedCheckMark: string
  strikeText: string
  bodyText: string
  mutedText: string
  brandText: string
  acceptBg: string
  acceptText: string
  // CON-215 Option B: Dismiss ghost button. `dismissText`
  // names the labeled button color; `dismissHoverBg` paints
  // the rounded-pill hover background. They diverge enough
  // from `mutedText` / `kebabHover` (which the kebab + meta
  // line use) that we model them explicitly.
  dismissText: string
  dismissHoverBg: string
  // Hairline border on ghost pills (CON-223 one-template
  // review) — mirrors SuggestionPalette.ghostBorder.
  ghostBorder: string
  kebabHover: string
  menuBg: string
  menuBorder: string
  menuText: string
  menuHover: string
  dangerText: string
  composerBg: string
  composerBorder: string
  inputBg: string
  inputBorder: string
  inputText: string
  addBg: string
  addText: string
}

const LIGHT_THEME: FollowUpTheme = {
  suggestedBg: "#FBF6EE",
  suggestedBorder: "#E5C896",
  rowBorder: "#E5DFD3",
  rowBg: "#FFFFFF",
  checkboxBorder: "#B86A2D",
  completedCheckBg: "#1F8A4C",
  completedCheckMark: "#FFFFFF",
  strikeText: "#9A8F7E",
  bodyText: "#1E1B17",
  mutedText: "#9A8F7E",
  brandText: "#B86A2D",
  acceptBg: "#B86A2D",
  acceptText: "#FFFFFF",
  dismissText: "#6B6357",
  dismissHoverBg: "#F0EAD9",
  ghostBorder: "#DDD6C7",
  kebabHover: "#F0EDE6",
  menuBg: "#FFFFFF",
  menuBorder: "#E5DFD3",
  menuText: "#1E1B17",
  menuHover: "#F5F1E9",
  dangerText: "#C53030",
  composerBg: "#FBF6EE",
  composerBorder: "#B86A2D",
  inputBg: "#FFFFFF",
  inputBorder: "#E5DFD3",
  inputText: "#1E1B17",
  addBg: "#1E1B17",
  addText: "#FFFFFF",
}

const DARK_THEME: FollowUpTheme = {
  suggestedBg: "#252019",
  suggestedBorder: "#3A352E",
  rowBorder: "#3A352E",
  rowBg: "#252019",
  checkboxBorder: "#E0A56A",
  completedCheckBg: "#22361F",
  completedCheckMark: "#7FD79A",
  strikeText: "#9A938A",
  bodyText: "#FBF7F0",
  mutedText: "#9A938A",
  brandText: "#E0A56A",
  acceptBg: "#E0A56A",
  acceptText: "#1E1B17",
  dismissText: "#C3BAB0",
  dismissHoverBg: "#2F2A24",
  ghostBorder: "#3A352E",
  kebabHover: "#2F2A24",
  menuBg: "#252019",
  menuBorder: "#3A352E",
  menuText: "#FBF7F0",
  menuHover: "#2F2A24",
  dangerText: "#E5736F",
  composerBg: "#221E18",
  composerBorder: "#E0A56A",
  inputBg: "#1E1B17",
  inputBorder: "#3A352E",
  inputText: "#FBF7F0",
  addBg: "#F4EFE6",
  addText: "#1E1B17",
}

// ── Method labels (the proposed / chosen channel) ──────────
// `methodLabel` is shared with the inbox summary's suggested
// next-step row (CON-204) — see `@/lib/follow-up-method`.
// "Thu 9:00" — short weekday + 24h time, matching the design.
function formatWhen(iso: string | null): string | null {
  if (!iso) return null
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return null
  const day = new Intl.DateTimeFormat("en-US", { weekday: "short" }).format(d)
  const time = new Intl.DateTimeFormat("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: false,
  }).format(d)
  return `${day} ${time}`
}

// A stable key per row. Accepted rows key on the Activity id;
// suggested / dismissed key on the verbatim title (the server
// has no id for them and the title is what we dismiss on).
function rowKey(row: FollowUpSuggestion): string {
  if (row.id != null) return `activity-${row.id}`
  return `${row.kind}-${row.title}`
}

function reloadShared() {
  const options: ReloadOptions & {
    preserveScroll?: boolean
    preserveState?: boolean
  } = {
    only: [...CALL_DETAIL_RELOAD_PROPS],
    preserveScroll: true,
    preserveState: true,
  }
  router.reload(options)
}

// The activities endpoint a follow-up writes to. Deal-bound
// when the call carries a deal; contact-bound otherwise. A
// deal id can be passed to override (Move to another deal).
function activitiesUrl(
  voiceCall: VoiceCallRecord,
  dealId?: number | null,
): string | null {
  if (dealId != null) return `/v2/deals/${dealId}/activities`
  if (voiceCall.deal?.id) return `/v2/deals/${voiceCall.deal.id}/activities`
  if (voiceCall.contact?.id) {
    return `/v2/contacts/${voiceCall.contact.id}/activities`
  }
  return null
}

// The endpoint that edits / completes / deletes an accepted
// Activity. Deal-bound rows route through the deal; contact-
// bound (deal_id null) through the contact.
function activityUrl(
  voiceCall: VoiceCallRecord,
  row: FollowUpSuggestion,
): string | null {
  if (row.id == null) return null
  if (row.dealId != null) {
    return `/v2/deals/${row.dealId}/activities/${row.id}`
  }
  if (voiceCall.contact?.id) {
    return `/v2/contacts/${voiceCall.contact.id}/activities/${row.id}`
  }
  return null
}

async function fetchJson(
  url: string,
  method: string,
  body?: Record<string, unknown>,
): Promise<unknown> {
  const response = await fetch(url, {
    method,
    headers: {
      Accept: "application/json",
      "X-CSRF-Token": getCsrfToken(),
      ...(body ? { "Content-Type": "application/json" } : {}),
    },
    credentials: "same-origin",
    ...(body ? { body: JSON.stringify(body) } : {}),
  })
  if (!response.ok) throw new Error(`Request failed: ${response.status}`)
  if (response.status === 204) return null
  return camelizeKeys(await response.json().catch(() => ({})))
}

export function CallFollowUps({
  voiceCall,
  theme,
  currentEmployee,
  ownerOptions,
}: CallFollowUpsProps) {
  void ownerOptions
  const { t } = useLingui()
  const palette = theme === "dark" ? DARK_THEME : LIGHT_THEME

  // Optimistic local list seeded from the server prop, re-seeded
  // whenever the prop identity changes (a live channel push or a
  // partial reload lands a fresh ordered list). We track the
  // last server snapshot so our own optimistic edits aren't
  // clobbered by an effect that re-runs on an unrelated render.
  const [rows, setRows] = useState<FollowUpSuggestion[]>(
    () => voiceCall.followUpSuggestions ?? [],
  )
  const serverRef = useRef(voiceCall.followUpSuggestions)
  useEffect(() => {
    if (serverRef.current === voiceCall.followUpSuggestions) return
    serverRef.current = voiceCall.followUpSuggestions
    setRows(voiceCall.followUpSuggestions ?? [])
  }, [voiceCall.followUpSuggestions])

  const [showDismissed, setShowDismissed] = useState(false)
  const [composerOpen, setComposerOpen] = useState(false)
  const [editingKey, setEditingKey] = useState<string | null>(null)

  const accepted = rows.filter(
    (r) => r.kind === "accepted_pending" || r.kind === "accepted_completed",
  )
  const suggested = rows.filter((r) => r.kind === "suggested")
  const pending = rows.filter((r) => r.kind === "accepted_pending")
  const completed = rows.filter((r) => r.kind === "accepted_completed")
  const dismissed = rows.filter((r) => r.kind === "dismissed")

  const targetName = voiceCall.deal?.name ?? voiceCall.contact?.name ?? null

  // Replace a row (matched by key) with a new row, or remove it
  // when `next` is null.
  const replaceRow = (key: string, next: FollowUpSuggestion | null) => {
    setRows((prev) => {
      const out: FollowUpSuggestion[] = []
      for (const r of prev) {
        if (rowKey(r) === key) {
          if (next) out.push(next)
        } else {
          out.push(r)
        }
      }
      return out
    })
  }

  const upsertRow = (key: string, next: FollowUpSuggestion) => {
    setRows((prev) => {
      if (prev.some((r) => rowKey(r) === key)) {
        return prev.map((r) => (rowKey(r) === key ? next : r))
      }
      return [...prev, next]
    })
  }

  // ── Accept a suggestion → create an Activity ─────────────
  const accept = async (row: FollowUpSuggestion) => {
    const url = activitiesUrl(voiceCall)
    if (!url) return
    const key = rowKey(row)
    const dueAt = row.dueAt ?? tomorrowAt9Iso()
    const method = row.method ?? "voice_call_outbound"

    // Optimistic: flip to a pending row with no real id yet.
    replaceRow(key, {
      id: null,
      title: row.title,
      kind: "accepted_pending",
      dueAt,
      method,
      dealId: voiceCall.deal?.id ?? null,
    })
    const optimisticKey = `accepted_pending-${row.title}`

    try {
      const created = (await fetchJson(url, "POST", {
        activity: {
          title: row.title,
          method,
          due_at: dueAt,
          assignee_id: currentEmployee.id,
          ai_suggested: true,
          // Accepting IS the confirmation — born confirmed so
          // no surface re-offers Accept (CON-223).
          confirmed: true,
          voice_call_id: voiceCall.id,
          source_suggestion_text: row.title,
        },
      })) as FollowUpSuggestion
      // Replace the placeholder with the real row carrying the id.
      replaceRow(optimisticKey, {
        id: created.id,
        title: created.title,
        kind: created.kind ?? "accepted_pending",
        dueAt: created.dueAt ?? dueAt,
        method: created.method ?? method,
        dealId: created.dealId ?? voiceCall.deal?.id ?? null,
      })
      const whenLabel = formatWhen(created.dueAt ?? dueAt)
      const createdId = created.id
      toast.success(t`Follow-up scheduled`, {
        description: whenLabel ? `${row.title} · ${whenLabel}` : row.title,
        duration: 6000,
        ...(createdId != null
          ? {
              action: {
                label: t`Undo`,
                onClick: () => void undoAccept(createdId, row),
              },
            }
          : {}),
      })
      reloadShared()
    } catch {
      // Roll back to the original suggested row.
      replaceRow(optimisticKey, row)
      toast.error(t`We couldn't schedule that. Please try again.`)
    }
  }

  const undoAccept = async (
    activityId: number,
    original: FollowUpSuggestion,
  ) => {
    const url = activityUrlForId(voiceCall, activityId)
    if (!url) return
    // Restore the suggested row immediately.
    replaceRow(`activity-${activityId}`, original)
    try {
      await fetchJson(url, "DELETE")
      reloadShared()
    } catch {
      toast.error(t`We couldn't undo that. Please try again.`)
    }
  }

  // ── Reject a suggestion → append to dismissed ────────────
  const reject = async (row: FollowUpSuggestion) => {
    const key = rowKey(row)
    replaceRow(key, { ...row, kind: "dismissed", id: null })
    try {
      await fetchJson(
        `/v2/voice_calls/${voiceCall.id}/follow_up_dismissals`,
        "POST",
        { title: row.title },
      )
      toast(t`Suggestion dismissed`, {
        icon: <DismissIcon color={palette.mutedText} />,
        description: row.title,
        duration: 6000,
        action: { label: t`Undo`, onClick: () => void bringBack(row.title) },
      })
      reloadShared()
    } catch {
      replaceRow(`dismissed-${row.title}`, row)
      toast.error(t`We couldn't dismiss that. Please try again.`)
    }
  }

  // ── Bring back a dismissed title → back to suggested ─────
  const bringBack = async (title: string) => {
    // Move it to the top of suggested.
    setRows((prev) => {
      const without = prev.filter(
        (r) => !(r.kind === "dismissed" && r.title === title),
      )
      return [
        {
          id: null,
          title,
          kind: "suggested" as const,
          dueAt: null,
          method: null,
          dealId: null,
        },
        ...without,
      ]
    })
    try {
      await fetchJson(
        `/v2/voice_calls/${voiceCall.id}/follow_up_dismissals/` +
          encodeURIComponent(title),
        "DELETE",
      )
      reloadShared()
    } catch {
      toast.error(t`We couldn't bring that back. Please try again.`)
    }
  }

  // ── Complete / uncomplete an accepted Activity ───────────
  const setCompleted = async (
    row: FollowUpSuggestion,
    completedNow: boolean,
  ) => {
    const url = activityUrl(voiceCall, row)
    if (!url || row.id == null) return
    const key = rowKey(row)
    const nextKind = completedNow ? "accepted_completed" : "accepted_pending"
    replaceRow(key, { ...row, kind: nextKind })
    try {
      const updated = (await fetchJson(url, "PATCH", {
        activity: { status: completedNow ? "completed" : "pending" },
      })) as FollowUpSuggestion
      replaceRow(`activity-${row.id}`, {
        id: updated.id,
        title: updated.title,
        kind: updated.kind ?? nextKind,
        dueAt: updated.dueAt ?? row.dueAt,
        method: updated.method ?? row.method,
        dealId: updated.dealId ?? row.dealId,
      })
      reloadShared()
    } catch {
      replaceRow(`activity-${row.id}`, row)
      toast.error(t`We couldn't update that. Please try again.`)
    }
  }

  // ── Edit an accepted Activity (title / date / method) ────
  const saveEdit = async (
    row: FollowUpSuggestion,
    patch: { title: string; dueAt: string; method: string | null },
  ) => {
    const url = activityUrl(voiceCall, row)
    if (!url || row.id == null) return
    const optimistic: FollowUpSuggestion = {
      ...row,
      title: patch.title,
      dueAt: patch.dueAt,
      method: patch.method,
    }
    replaceRow(rowKey(row), optimistic)
    setEditingKey(null)
    try {
      const updated = (await fetchJson(url, "PATCH", {
        activity: {
          title: patch.title,
          due_at: patch.dueAt,
          method: patch.method,
        },
      })) as FollowUpSuggestion
      replaceRow(`activity-${row.id}`, {
        id: updated.id,
        title: updated.title,
        kind: updated.kind ?? row.kind,
        dueAt: updated.dueAt ?? patch.dueAt,
        method: updated.method ?? patch.method,
        dealId: updated.dealId ?? row.dealId,
      })
      reloadShared()
    } catch {
      replaceRow(`activity-${row.id}`, row)
      toast.error(t`We couldn't save that. Please try again.`)
    }
  }

  // ── Move an accepted Activity to another deal ────────────
  const moveToDeal = async (row: FollowUpSuggestion, dealId: number) => {
    const url = activityUrl(voiceCall, row)
    if (!url || row.id == null) return
    replaceRow(rowKey(row), { ...row, dealId })
    try {
      const updated = (await fetchJson(url, "PATCH", {
        activity: { deal_id: dealId },
      })) as FollowUpSuggestion
      replaceRow(`activity-${row.id}`, {
        id: updated.id,
        title: updated.title,
        kind: updated.kind ?? row.kind,
        dueAt: updated.dueAt ?? row.dueAt,
        method: updated.method ?? row.method,
        dealId: updated.dealId ?? dealId,
      })
      reloadShared()
    } catch {
      replaceRow(`activity-${row.id}`, row)
      toast.error(t`We couldn't move that. Please try again.`)
    }
  }

  // ── Delete an accepted Activity ──────────────────────────
  const deleteActivity = async (
    row: FollowUpSuggestion,
    alsoDismiss: boolean,
  ) => {
    const url = activityUrl(voiceCall, row)
    if (!url || row.id == null) return
    const key = rowKey(row)
    replaceRow(key, null)
    try {
      await fetchJson(url, "DELETE")
      // "I accepted by accident" — keep it out of the regenerate
      // cycle by also dismissing its source suggestion text.
      if (alsoDismiss && row.title) {
        await fetchJson(
          `/v2/voice_calls/${voiceCall.id}/follow_up_dismissals`,
          "POST",
          { title: row.title },
        )
      }
      reloadShared()
    } catch {
      upsertRow(key, row)
      toast.error(t`We couldn't delete that. Please try again.`)
    }
  }

  // ── Add a follow-up from the composer → accepted_pending ──
  const addFollowUp = async (
    title: string,
    dueIso: string,
    method: string | null,
    dealId: number | null,
  ) => {
    const url = activitiesUrl(voiceCall, dealId)
    if (!url) return
    setComposerOpen(false)
    const tempKey = `accepted_pending-${title}`
    upsertRow(tempKey, {
      id: null,
      title,
      kind: "accepted_pending",
      dueAt: dueIso,
      method,
      dealId: dealId ?? voiceCall.deal?.id ?? null,
    })
    try {
      const created = (await fetchJson(url, "POST", {
        activity: {
          title,
          method,
          due_at: dueIso,
          assignee_id: currentEmployee.id,
          ai_suggested: false,
          voice_call_id: voiceCall.id,
        },
      })) as FollowUpSuggestion
      replaceRow(tempKey, {
        id: created.id,
        title: created.title,
        kind: created.kind ?? "accepted_pending",
        dueAt: created.dueAt ?? dueIso,
        method: created.method ?? method,
        dealId: created.dealId ?? dealId ?? voiceCall.deal?.id ?? null,
      })
      reloadShared()
    } catch {
      replaceRow(tempKey, null)
      toast.error(t`We couldn't add that. Please try again.`)
    }
  }

  // ── Order the visible rows: pending → suggested → completed.
  // The server already sorts; we preserve its order but split
  // dismissed out (collapsed) and keep our optimistic shuffles
  // stable.
  const visibleRows = useMemo(() => {
    return [...pending, ...suggested, ...completed]
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows])

  const isDark = theme === "dark"
  const hasAny = visibleRows.length > 0 || dismissed.length > 0

  return (
    <section
      data-testid="call-follow-ups"
      data-theme={theme}
      className={
        isDark
          ? "flex flex-col gap-[7px]"
          : "flex flex-col gap-2.5 py-4 px-5 border-b border-solid border-[#F0EDE6]"
      }
    >
      <div className="flex items-center gap-1.5">
        <span
          className={
            isDark
              ? "grow text-[11px] font-semibold uppercase tracking-[0.04em]"
              : "font-medium text-[11px] leading-3.5 tracking-[0.04em] uppercase"
          }
          style={{ color: palette.mutedText }}
        >
          <Trans>Follow-ups</Trans>
        </span>
        {accepted.length > 0 ? (
          <span
            className="text-[11px] leading-3.5"
            style={{ color: palette.mutedText }}
          >
            {pending.length}
          </span>
        ) : null}
      </div>

      {!hasAny && !composerOpen ? (
        <p className="text-[13px] leading-5" style={{ color: palette.mutedText }}>
          {targetName
            ? t`No follow-ups yet. Add one to keep things moving.`
            : t`No follow-ups yet.`}
        </p>
      ) : null}

      {visibleRows.map((row) => {
        const key = rowKey(row)
        if (row.kind === "suggested") {
          return (
            <SuggestedRow
              key={key}
              row={row}
              palette={palette}
              onAccept={() => void accept(row)}
              onReject={() => void reject(row)}
            />
          )
        }
        if (row.kind === "accepted_completed") {
          return (
            <CompletedRow
              key={key}
              row={row}
              palette={palette}
              onUncheck={() => void setCompleted(row, false)}
              onDelete={(alsoDismiss) => void deleteActivity(row, alsoDismiss)}
            />
          )
        }
        // accepted_pending
        if (editingKey === key) {
          return (
            <EditRow
              key={key}
              row={row}
              palette={palette}
              onSave={(patch) => void saveEdit(row, patch)}
              onCancel={() => setEditingKey(null)}
            />
          )
        }
        return (
          <PendingRow
            key={key}
            row={row}
            palette={palette}
            targetName={targetName}
            voiceCall={voiceCall}
            onComplete={() => void setCompleted(row, true)}
            onEdit={() => setEditingKey(key)}
            onMove={(dealId) => void moveToDeal(row, dealId)}
            onDelete={(alsoDismiss) => void deleteActivity(row, alsoDismiss)}
          />
        )
      })}

      {composerOpen ? (
        <FollowUpComposer
          voiceCall={voiceCall}
          palette={palette}
          onAdd={addFollowUp}
          onCancel={() => setComposerOpen(false)}
        />
      ) : null}

      {/* Footer: + Add follow-up · Show dismissed (N) */}
      <div className="flex items-center gap-3">
        {!composerOpen ? (
          <button
            type="button"
            data-testid="follow-up-add"
            onClick={() => setComposerOpen(true)}
            className="flex items-center gap-1 text-[12px] leading-4 font-medium hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand rounded"
            style={{ color: palette.brandText }}
          >
            <PlusGlyph color={palette.brandText} />
            <Trans>Add follow-up</Trans>
          </button>
        ) : null}
        {dismissed.length > 0 ? (
          <button
            type="button"
            data-testid="follow-up-show-dismissed"
            onClick={() => setShowDismissed((v) => !v)}
            className="ml-auto text-[12px] leading-4 font-medium hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand rounded"
            style={{ color: palette.mutedText }}
          >
            {showDismissed
              ? t`Hide dismissed`
              : t`Show dismissed (${dismissed.length}) →`}
          </button>
        ) : null}
      </div>

      {showDismissed && dismissed.length > 0 ? (
        <div className="flex flex-col gap-2" data-testid="follow-up-dismissed-list">
          {dismissed.map((row) => (
            <DismissedRow
              key={rowKey(row)}
              row={row}
              palette={palette}
              onBringBack={() => void bringBack(row.title)}
            />
          ))}
        </div>
      ) : null}
    </section>
  )
}

// activityUrl helper that takes a raw id (used by undoAccept,
// where the row was already optimistically replaced).
function activityUrlForId(
  voiceCall: VoiceCallRecord,
  activityId: number,
): string | null {
  if (voiceCall.deal?.id) {
    return `/v2/deals/${voiceCall.deal.id}/activities/${activityId}`
  }
  if (voiceCall.contact?.id) {
    return `/v2/contacts/${voiceCall.contact.id}/activities/${activityId}`
  }
  return null
}

// ── Suggested row ──────────────────────────────────────────
// Dashed amber border, cream/amber tint, NO checkbox.
//
// Option B layout (CON-215) now renders via the shared
// `SuggestionCard` (CON-223) — the canonical suggestion row
// at `@/components/v2/suggestions/suggestion-card`. The
// `FollowUpTheme` palette passes straight through: its key
// names intentionally match `SuggestionPalette`, so the dark
// after-call card and the light detail panel share markup.
// Brand crumb is "from your call"; due time + method ride as
// muted meta facts after it.
function SuggestedRow({
  row,
  palette,
  onAccept,
  onReject,
}: {
  row: FollowUpSuggestion
  palette: FollowUpTheme
  onAccept: () => void
  onReject: () => void
}) {
  const { t, i18n } = useLingui()
  return (
    <SuggestionCard
      palette={palette}
      title={row.title}
      crumb={{ label: t`from your call` }}
      metaFacts={[formatWhen(row.dueAt), methodLabel(row.method, i18n)]}
      onAccept={onAccept}
      onDismiss={onReject}
      testIds={{
        root: "follow-up-row-suggested",
        accept: "follow-up-accept",
        dismiss: "follow-up-reject",
      }}
    />
  )
}

// ── Pending (accepted, open) row ───────────────────────────
function PendingRow({
  row,
  palette,
  targetName,
  voiceCall,
  onComplete,
  onEdit,
  onMove,
  onDelete,
}: {
  row: FollowUpSuggestion
  palette: FollowUpTheme
  targetName: string | null
  voiceCall: VoiceCallRecord
  onComplete: () => void
  onEdit: () => void
  onMove: (dealId: number) => void
  onDelete: (alsoDismiss: boolean) => void
}) {
  const { t } = useLingui()
  const when = formatWhen(row.dueAt)
  const sub = [when, targetName, t`✨ from your call`].filter(Boolean).join(" · ")
  return (
    <div
      data-testid="follow-up-row-pending"
      className="flex items-start gap-2.5 rounded-[11px] py-2.5 px-3 border border-solid"
      style={{ backgroundColor: palette.rowBg, borderColor: palette.rowBorder }}
    >
      <button
        type="button"
        role="checkbox"
        aria-checked={false}
        aria-label={row.title}
        data-testid="follow-up-complete"
        onClick={onComplete}
        className="mt-0.5 shrink-0 size-4 rounded-[5px] border-[1.5px] border-solid focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand"
        style={{ borderColor: palette.checkboxBorder }}
      />
      <button
        type="button"
        data-testid="follow-up-edit-trigger"
        onClick={onEdit}
        className="flex flex-col grow min-w-0 text-left"
      >
        <span
          className="text-[13px] leading-[18px] break-words"
          style={{ color: palette.bodyText }}
        >
          {row.title}
        </span>
        <span
          className="text-[11px] leading-3.5 truncate"
          style={{ color: palette.mutedText }}
        >
          {sub}
        </span>
      </button>
      <RowKebab palette={palette}>
        {(close) => (
          <PendingMenu
            palette={palette}
            voiceCall={voiceCall}
            onEdit={() => {
              close()
              onEdit()
            }}
            onMove={(dealId) => {
              close()
              onMove(dealId)
            }}
            onDelete={(alsoDismiss) => {
              close()
              onDelete(alsoDismiss)
            }}
            sourceTitle={row.title}
          />
        )}
      </RowKebab>
    </div>
  )
}

// ── Completed row ──────────────────────────────────────────
function CompletedRow({
  row,
  palette,
  onUncheck,
  onDelete,
}: {
  row: FollowUpSuggestion
  palette: FollowUpTheme
  onUncheck: () => void
  onDelete: (alsoDismiss: boolean) => void
}) {
  return (
    <div
      data-testid="follow-up-row-completed"
      className="flex items-start gap-2.5 rounded-[11px] py-2.5 px-3 border border-solid"
      style={{ backgroundColor: palette.rowBg, borderColor: palette.rowBorder }}
    >
      <button
        type="button"
        role="checkbox"
        aria-checked={true}
        aria-label={row.title}
        data-testid="follow-up-uncheck"
        onClick={onUncheck}
        className="mt-0.5 flex items-center justify-center shrink-0 size-4 rounded-[5px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand"
        style={{ backgroundColor: palette.completedCheckBg }}
      >
        <CheckGlyph color={palette.completedCheckMark} />
      </button>
      <div className="flex flex-col grow min-w-0">
        <span
          className="text-[13px] leading-[18px] line-through break-words"
          style={{ color: palette.strikeText }}
        >
          {row.title}
        </span>
        <span
          className="text-[11px] leading-3.5"
          style={{ color: palette.strikeText }}
        >
          <Trans>Completed</Trans>
        </span>
      </div>
      <RowKebab palette={palette}>
        {(close) => (
          <CompletedMenu
            palette={palette}
            onUncheck={() => {
              close()
              onUncheck()
            }}
            onDelete={(alsoDismiss) => {
              close()
              onDelete(alsoDismiss)
            }}
            sourceTitle={row.title}
          />
        )}
      </RowKebab>
    </div>
  )
}

// ── Dismissed row (inside the expanded list) ───────────────
function DismissedRow({
  row,
  palette,
  onBringBack,
}: {
  row: FollowUpSuggestion
  palette: FollowUpTheme
  onBringBack: () => void
}) {
  return (
    <div
      data-testid="follow-up-row-dismissed"
      className="flex items-start gap-2.5 rounded-[11px] py-2.5 px-3 border border-solid opacity-70"
      style={{ borderColor: palette.rowBorder }}
    >
      <div className="flex flex-col grow min-w-0">
        <span
          className="text-[13px] leading-[18px] line-through break-words"
          style={{ color: palette.strikeText }}
        >
          {row.title}
        </span>
        <span
          className="flex items-center gap-1 text-[11px] leading-3.5"
          style={{ color: palette.mutedText }}
        >
          <SparkleGlyph color={palette.mutedText} />
          <Trans>AI suggested · dismissed</Trans>
        </span>
      </div>
      <button
        type="button"
        data-testid="follow-up-bring-back"
        onClick={onBringBack}
        className="flex shrink-0 items-center gap-1 text-[12px] leading-4 font-medium hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand rounded"
        style={{ color: palette.brandText }}
      >
        <ResetGlyph color={palette.brandText} />
        <Trans>Bring back</Trans>
      </button>
    </div>
  )
}

// ── Kebab (portaled menu) ──────────────────────────────────
function RowKebab({
  palette,
  children,
}: {
  palette: FollowUpTheme
  children: (close: () => void) => ReactNode
}) {
  const { t } = useLingui()
  const triggerRef = useRef<HTMLButtonElement>(null)
  const [open, setOpen] = useState(false)
  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={t`Follow-up actions`}
        data-testid="follow-up-kebab"
        onClick={() => setOpen((v) => !v)}
        className="flex shrink-0 items-center justify-center rounded-md size-6 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand"
        style={{ color: palette.mutedText }}
      >
        <KebabGlyph color={palette.mutedText} />
      </button>
      <PortalPopover
        open={open}
        onClose={() => setOpen(false)}
        triggerRef={triggerRef}
        placement="bottom-end"
        zClassName="z-[60]"
        ariaLabel={t`Follow-up actions`}
        className="w-52 rounded-xl border border-solid shadow-[0_8px_24px_rgba(30,27,23,0.18)] py-1.5"
      >
        <div
          role="menu"
          className="rounded-xl"
          style={{ backgroundColor: palette.menuBg, borderColor: palette.menuBorder }}
        >
          {children(() => setOpen(false))}
        </div>
      </PortalPopover>
    </>
  )
}

function PendingMenu({
  palette,
  voiceCall,
  onEdit,
  onMove,
  onDelete,
  sourceTitle,
}: {
  palette: FollowUpTheme
  voiceCall: VoiceCallRecord
  onEdit: () => void
  onMove: (dealId: number) => void
  onDelete: (alsoDismiss: boolean) => void
  sourceTitle: string
}) {
  const [confirming, setConfirming] = useState(false)
  const [moving, setMoving] = useState(false)

  if (confirming) {
    return (
      <DeleteConfirm
        palette={palette}
        sourceTitle={sourceTitle}
        onConfirm={onDelete}
        onCancel={() => setConfirming(false)}
      />
    )
  }
  if (moving) {
    return (
      <MoveDealList
        palette={palette}
        voiceCall={voiceCall}
        onPick={onMove}
        onBack={() => setMoving(false)}
      />
    )
  }
  return (
    <>
      <MenuItem palette={palette} testid="follow-up-menu-edit" onClick={onEdit}>
        <Trans>Edit</Trans>
      </MenuItem>
      <MenuItem
        palette={palette}
        testid="follow-up-menu-move"
        onClick={() => setMoving(true)}
      >
        <Trans>Move to another deal</Trans>
      </MenuItem>
      <MenuItem
        palette={palette}
        testid="follow-up-menu-delete"
        danger
        onClick={() => setConfirming(true)}
      >
        <Trans>Delete</Trans>
      </MenuItem>
    </>
  )
}

function CompletedMenu({
  palette,
  onUncheck,
  onDelete,
  sourceTitle,
}: {
  palette: FollowUpTheme
  onUncheck: () => void
  onDelete: (alsoDismiss: boolean) => void
  sourceTitle: string
}) {
  const [confirming, setConfirming] = useState(false)
  if (confirming) {
    return (
      <DeleteConfirm
        palette={palette}
        sourceTitle={sourceTitle}
        onConfirm={onDelete}
        onCancel={() => setConfirming(false)}
      />
    )
  }
  return (
    <>
      <MenuItem
        palette={palette}
        testid="follow-up-menu-uncheck"
        onClick={onUncheck}
      >
        <Trans>Uncheck</Trans>
      </MenuItem>
      <MenuItem
        palette={palette}
        testid="follow-up-menu-delete"
        danger
        onClick={() => setConfirming(true)}
      >
        <Trans>Delete</Trans>
      </MenuItem>
    </>
  )
}

// The delete confirmation with the "Also dismiss this
// suggestion?" checkbox (default checked).
function DeleteConfirm({
  palette,
  sourceTitle,
  onConfirm,
  onCancel,
}: {
  palette: FollowUpTheme
  sourceTitle: string
  onConfirm: (alsoDismiss: boolean) => void
  onCancel: () => void
}) {
  const { t } = useLingui()
  const [alsoDismiss, setAlsoDismiss] = useState(true)
  // Heading + quoted subtitle come from the shared
  // `deletePromptCopy` so the confirm reads identically to
  // every other delete surface (Today, deal feed, inbox rail).
  const { heading, subtitle } = deletePromptCopy(
    "follow-up",
    sourceTitle,
    t,
  )
  return (
    <div className="flex flex-col gap-2.5 p-3" data-testid="follow-up-delete-confirm">
      <span className="text-[13px] font-medium" style={{ color: palette.menuText }}>
        {heading}
      </span>
      {subtitle ? (
        <span
          data-testid="follow-up-delete-quote"
          className="text-[12px] leading-4 truncate"
          style={{ color: palette.mutedText }}
          title={subtitle}
        >
          {subtitle}
        </span>
      ) : null}
      <label
        className="flex items-center gap-2 text-[12px] leading-4 cursor-pointer"
        style={{ color: palette.mutedText }}
      >
        <input
          type="checkbox"
          checked={alsoDismiss}
          data-testid="follow-up-delete-also-dismiss"
          onChange={(e) => setAlsoDismiss(e.target.checked)}
          className="size-3.5 accent-current"
        />
        <Trans>Also dismiss this suggestion?</Trans>
      </label>
      <div className="flex items-center gap-2">
        <button
          type="button"
          data-testid="follow-up-delete-confirm-button"
          onClick={() => onConfirm(alsoDismiss)}
          className="rounded-lg py-1.5 px-3 text-[12px] font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand"
          style={{ backgroundColor: palette.dangerText, color: "#FFFFFF" }}
        >
          <Trans>Delete</Trans>
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="text-[12px] font-medium hover:underline"
          style={{ color: palette.mutedText }}
        >
          <Trans>Cancel</Trans>
        </button>
      </div>
    </div>
  )
}

// The "Move to another deal" picker inside the kebab menu —
// fetches the contact's deals from the same endpoint the panel
// uses.
function MoveDealList({
  palette,
  voiceCall,
  onPick,
  onBack,
}: {
  palette: FollowUpTheme
  voiceCall: VoiceCallRecord
  onPick: (dealId: number) => void
  onBack: () => void
}) {
  const [deals, setDeals] = useState<VoiceCallDeal[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    fetch(`/v2/channels/voice/calls/${voiceCall.id}/deals`, {
      headers: { Accept: "application/json" },
      credentials: "same-origin",
    })
      .then((r) => {
        if (!r.ok) throw new Error("deals")
        return r.json()
      })
      .then((raw) => {
        if (cancelled) return
        const data = camelizeKeys(raw) as CallDealsResponse
        setDeals(data.deals ?? [])
      })
      .catch(() => {
        if (!cancelled) setDeals([])
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [voiceCall.id])

  return (
    <div className="flex flex-col" data-testid="follow-up-move-list">
      <button
        type="button"
        onClick={onBack}
        className="flex items-center gap-1 py-1.5 px-3 text-left text-[12px] font-medium hover:underline"
        style={{ color: palette.brandText }}
      >
        <Trans>← Back</Trans>
      </button>
      {loading ? (
        <p className="py-2 px-3 text-[13px]" style={{ color: palette.mutedText }}>
          <Trans>Loading deals…</Trans>
        </p>
      ) : deals.length === 0 ? (
        <p className="py-2 px-3 text-[13px]" style={{ color: palette.mutedText }}>
          <Trans>No other deals to move this to yet.</Trans>
        </p>
      ) : (
        deals.map((deal) => (
          <button
            key={deal.id}
            type="button"
            role="menuitem"
            data-testid={`follow-up-move-deal-${deal.id}`}
            onClick={() => onPick(deal.id)}
            className="flex flex-col gap-0.5 py-2 px-3 text-left"
            style={{ color: palette.menuText }}
            onMouseEnter={(e) =>
              (e.currentTarget.style.backgroundColor = palette.menuHover)
            }
            onMouseLeave={(e) =>
              (e.currentTarget.style.backgroundColor = "transparent")
            }
          >
            <span className="text-[13px] leading-[18px] truncate">
              {deal.name}
            </span>
            {deal.stage ? (
              <span
                className="text-[11px] leading-3.5 truncate"
                style={{ color: palette.mutedText }}
              >
                {deal.stage}
              </span>
            ) : null}
          </button>
        ))
      )}
    </div>
  )
}

function MenuItem({
  palette,
  testid,
  danger = false,
  onClick,
  children,
}: {
  palette: FollowUpTheme
  testid: string
  danger?: boolean
  onClick: () => void
  children: ReactNode
}) {
  return (
    <button
      type="button"
      role="menuitem"
      data-testid={testid}
      onClick={onClick}
      className="flex w-full items-center py-2 px-3 text-left text-[13px] leading-[18px] focus-visible:outline-none"
      style={{ color: danger ? palette.dangerText : palette.menuText }}
      onMouseEnter={(e) =>
        (e.currentTarget.style.backgroundColor = palette.menuHover)
      }
      onMouseLeave={(e) =>
        (e.currentTarget.style.backgroundColor = "transparent")
      }
    >
      {children}
    </button>
  )
}

// ── Inline edit (title / date / method) ────────────────────
function EditRow({
  row,
  palette,
  onSave,
  onCancel,
}: {
  row: FollowUpSuggestion
  palette: FollowUpTheme
  onSave: (patch: {
    title: string
    dueAt: string
    method: string | null
  }) => void
  onCancel: () => void
}) {
  const { t, i18n } = useLingui()
  const [title, setTitle] = useState(row.title)
  const [when, setWhen] = useState(
    row.dueAt ? isoToLocalInput(row.dueAt) : isoToLocalInput(tomorrowAt9Iso()),
  )
  const [method, setMethod] = useState<string>(
    row.method ?? "voice_call_outbound",
  )
  const canSave = title.trim().length > 0
  return (
    <div
      data-testid="follow-up-edit"
      className="flex flex-col gap-2.5 rounded-[11px] py-3 px-3.5 border border-solid"
      style={{ backgroundColor: palette.composerBg, borderColor: palette.composerBorder }}
    >
      <input
        type="text"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        aria-label={t`Follow-up`}
        className="w-full rounded-lg py-2 px-3 border border-solid text-[13px] leading-[18px] focus:outline-none focus-visible:ring-2 focus-visible:ring-brand"
        style={{ backgroundColor: palette.inputBg, borderColor: palette.inputBorder, color: palette.inputText }}
      />
      <div className="flex items-center gap-2">
        <input
          type="datetime-local"
          value={when}
          onChange={(e) => setWhen(e.target.value)}
          aria-label={t`Due date`}
          className="rounded-lg py-1.5 px-2.5 border border-solid text-[12px] leading-4 [color-scheme:dark] focus:outline-none"
          style={{ backgroundColor: palette.inputBg, borderColor: palette.inputBorder, color: palette.inputText }}
        />
        <select
          value={method}
          onChange={(e) => setMethod(e.target.value)}
          aria-label={t`Method`}
          className="rounded-lg py-1.5 px-2.5 border border-solid text-[12px] leading-4 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand"
          style={{ backgroundColor: palette.inputBg, borderColor: palette.inputBorder, color: palette.inputText }}
        >
          {COMPOSER_METHODS.map((m) => (
            <option key={m.value} value={m.value}>
              {i18n._(m.label)}
            </option>
          ))}
        </select>
      </div>
      <div className="flex items-center gap-2">
        <button
          type="button"
          data-testid="follow-up-edit-save"
          disabled={!canSave}
          onClick={() =>
            onSave({
              title: title.trim(),
              dueAt: localInputToIso(when),
              method,
            })
          }
          className="rounded-lg py-2 px-4 text-[13px] leading-[18px] font-medium disabled:opacity-50"
          style={{ backgroundColor: palette.addBg, color: palette.addText }}
        >
          <Trans>Save</Trans>
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="text-[13px] leading-[18px] hover:underline"
          style={{ color: palette.mutedText }}
        >
          <Trans>Cancel</Trans>
        </button>
      </div>
    </div>
  )
}

// ── + Add follow-up composer ───────────────────────────────
function FollowUpComposer({
  voiceCall,
  palette,
  onAdd,
  onCancel,
}: {
  voiceCall: VoiceCallRecord
  palette: FollowUpTheme
  onAdd: (
    title: string,
    dueIso: string,
    method: string | null,
    dealId: number | null,
  ) => void
  onCancel: () => void
}) {
  const { t, i18n } = useLingui()
  const [title, setTitle] = useState("")
  const [when, setWhen] = useState(isoToLocalInput(tomorrowAt9Iso()))
  const [method, setMethod] = useState<string>("voice_call_outbound")
  const canAdd = title.trim().length > 0
  return (
    <div
      data-testid="follow-up-composer"
      className="flex flex-col gap-2.5 rounded-[11px] py-3 px-3.5 border border-solid"
      style={{ backgroundColor: palette.composerBg, borderColor: palette.composerBorder }}
    >
      <span
        className="text-[11px] leading-3.5 tracking-[0.04em] uppercase font-semibold"
        style={{ color: palette.brandText }}
      >
        <Trans>New follow-up</Trans>
      </span>
      <input
        type="text"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        aria-label={t`Follow-up`}
        placeholder={t`What's the follow-up?`}
        className="w-full rounded-lg py-2 px-3 border border-solid text-[13px] leading-[18px] focus:outline-none focus-visible:ring-2 focus-visible:ring-brand"
        style={{ backgroundColor: palette.inputBg, borderColor: palette.inputBorder, color: palette.inputText }}
      />
      <div className="flex items-center gap-2">
        <input
          type="datetime-local"
          value={when}
          onChange={(e) => setWhen(e.target.value)}
          aria-label={t`Due date`}
          className="rounded-lg py-1.5 px-2.5 border border-solid text-[12px] leading-4 [color-scheme:dark] focus:outline-none"
          style={{ backgroundColor: palette.inputBg, borderColor: palette.inputBorder, color: palette.inputText }}
        />
        <select
          value={method}
          onChange={(e) => setMethod(e.target.value)}
          aria-label={t`Method`}
          className="rounded-lg py-1.5 px-2.5 border border-solid text-[12px] leading-4 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand"
          style={{ backgroundColor: palette.inputBg, borderColor: palette.inputBorder, color: palette.inputText }}
        >
          {COMPOSER_METHODS.map((m) => (
            <option key={m.value} value={m.value}>
              {i18n._(m.label)}
            </option>
          ))}
        </select>
      </div>
      {voiceCall.deal ? (
        <span className="text-[11px] leading-3.5" style={{ color: palette.mutedText }}>
          {t`On ${voiceCall.deal.name}`}
        </span>
      ) : null}
      <div className="flex items-center gap-2">
        <button
          type="button"
          data-testid="follow-up-composer-add"
          disabled={!canAdd}
          onClick={() =>
            onAdd(
              title.trim(),
              localInputToIso(when),
              method || null,
              voiceCall.deal?.id ?? null,
            )
          }
          className="rounded-lg py-2 px-4 text-[13px] leading-[18px] font-medium disabled:opacity-50"
          style={{ backgroundColor: palette.addBg, color: palette.addText }}
        >
          <Trans>Add</Trans>
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="text-[13px] leading-[18px] hover:underline"
          style={{ color: palette.mutedText }}
        >
          <Trans>Cancel</Trans>
        </button>
      </div>
    </div>
  )
}

// ── Glyphs ─────────────────────────────────────────────────
function PlusGlyph({ color }: { color: string }) {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M12 5v14M5 12h14"
        stroke={color}
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  )
}

function SparkleGlyph({ color }: { color: string }) {
  return (
    <svg
      width="10"
      height="10"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden
      className="shrink-0"
    >
      <path
        d="M12 2L14.5 9.5L22 12L14.5 14.5L12 22L9.5 14.5L2 12L9.5 9.5L12 2Z"
        fill={color}
      />
    </svg>
  )
}

function CheckGlyph({ color }: { color: string }) {
  return (
    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M5 12l5 5L19 7"
        stroke={color}
        strokeWidth="2.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

function DismissIcon({ color }: { color: string }) {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M6 6l12 12M18 6L6 18"
        stroke={color}
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  )
}

function ResetGlyph({ color }: { color: string }) {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M3 8a9 9 0 1 1-1 4"
        stroke={color}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M3 4v4h4"
        stroke={color}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

function KebabGlyph({ color }: { color: string }) {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
      <circle cx="12" cy="5" r="1.6" fill={color} />
      <circle cx="12" cy="12" r="1.6" fill={color} />
      <circle cx="12" cy="19" r="1.6" fill={color} />
    </svg>
  )
}
